// src/routes/cr/CRBattles.tsx
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';

import { useCrLinkDaLimulo } from '@/hooks/useCrLinkDaLimulo';
import { useHaptics } from '@/hooks/useHaptics';
import { useBattlesQuery, useProfileMeQuery } from '@/services/qbotApi';
import { BattlesListView } from '@/components/cr/BattlesListView';
import { SectionCard } from '@/components/hub/SectionCard';
import { Segmented } from '@/components/common/Segmented';
import { BattlesStats } from '@/components/cr/BattlesStats';
import { cn } from '@/lib/cn';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setBattlesView } from '@/store/core.slice';
import type { CRBattle, QbotApiError } from '@/types/qbot';

function isQbotApiError(err: unknown): err is QbotApiError {
  return typeof err === 'object' && err !== null && 'data' in err;
}

type BattlesView = 'list' | 'stats';

/**
 * Battles surface — a segmented List | Stats view over ONE shared 25-battle
 * window. Tag comes from /profile/me (no tag in the URL — the user wants their
 * own data); no linked player → redirect to /cr/link.
 *
 * Refresh sends `fresh=true` (a distinct RTK cache key) so qbot bypasses its
 * cache + the qCrC collector and reads the log directly from Supercell. Both
 * views read the same query, so a refresh updates List and Stats together.
 */
/**
 * L'invito a collegare l'account, DENTRO la superficie invece che al posto suo.
 *
 * Stessa forma di quello che `DecksPanel` mostra da mesi — di proposito: sono lo stesso fatto
 * («non hai un account collegato») nella stessa Hub, e due inviti diversi per la stessa cosa
 * si leggono come due problemi diversi. Il tocco porta a `href` — `/cr/link` di norma, la creazione
 * del profilo Limulo (`limuloProfileCreateUrl`) quando Limulo e' la fonte — **senza `replace`**: la
 * voce della Hub resta nella history, quindi da li' si torna indietro.
 */
function InvitoACollegare({ href = '/cr/link' }: { href?: string }) {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const navigate = useNavigate();
  return (
    <div className="px-4 py-6">
      <div className="flex items-center justify-between gap-2 rounded-lg border border-brand/40 bg-brand/10 px-2.5 py-2">
        <p className="min-w-0 text-[11px] leading-snug text-fg-muted">
          {t('battles.link.prompt')}
        </p>
        <button
          type="button"
          onClick={() => {
            haptics.impact('light');
            navigate(href);
          }}
          className="shrink-0 rounded-md bg-brand px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-brand-fg"
        >
          {t('battles.link.cta')}
        </button>
      </div>
    </div>
  );
}

export function CRBattles({ embedded = false }: { embedded?: boolean } = {}) {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const view = useAppSelector((s) => s.core.battlesView);
  const [fresh, setFresh] = useState(false);

  // 🔴 Con Limulo come fonte «collegato» e il TAG vengono dal profilo CR di LIMULO, non da
  // `/profile/me` di QBot: il Sit&Go scrive su Limulo e QBot legge una tabella sua (`SavedPlayer`),
  // quindi chi aveva collegato dal Sit&Go vedeva ancora «Collega». Il tag va a
  // `GET /battles/:tag` esplicito, invece di dipendere da `/profile/me`.
  // ⇒ Nel ramo la query di QBot e' in `skip`, e il «collega» compare SOLO se il profilo Limulo
  // manca a lettura riuscita e porta alla strada del Sit&Go (`limuloProfileCreateUrl`), non a
  // `/cr/link` (che su staging finisce su qduels di produzione e non puo' riuscire). Se la lettura
  // fallisce: un errore di lettura, mai il banner. Fuori dal ramo (`kind: 'off'`) e' tutto come prima.
  const crLink = useCrLinkDaLimulo();
  const fromLimulo = crLink.kind !== 'off';
  const qbotProfile = useProfileMeQuery(undefined, { skip: fromLimulo });
  const profile = qbotProfile.data;
  const profileLoading = fromLimulo ? crLink.kind === 'loading' : qbotProfile.isLoading;
  const profileError = fromLimulo ? undefined : qbotProfile.error;
  const tag = fromLimulo
    ? crLink.kind === 'linked'
      ? crLink.tag
      : ''
    : (profile?.savedTag ?? profile?.player?.tag?.replace('#', '') ?? '');

  const { data, isFetching, error, refetch } = useBattlesQuery(
    { tag, page: 1, pageSize: 25, fresh },
    { skip: !tag },
  );
  const battles: CRBattle[] = data?.battles ?? [];

  const onRefresh = () => {
    if (isFetching) return;
    haptics.impact('light');
    if (fresh) refetch(); else setFresh(true);
  };

  if (profileLoading) {
    return <div className="px-4 py-6 text-sm text-fg-muted">{t('battles.loading')}</div>;
  }
  if (crLink.kind === 'notLinked') {
    // Stessa regola del ramo qbot qui sotto: dentro la Hub non si naviga via.
    if (embedded) return <InvitoACollegare href={crLink.createHref} />;
    return <Navigate to={crLink.createHref} replace />;
  }
  // «Non ho potuto leggere» — o il profilo c'e' ma senza tag, e senza tag non si chiede niente:
  // non e' «non collegato», quindi niente banner.
  if (crLink.kind === 'unknown' || (crLink.kind === 'linked' && crLink.tag === '')) {
    return (
      <div className="px-4 py-6">
        <p className="rounded-md bg-rose-500/10 p-3 text-sm text-rose-300">
          {crLink.kind === 'unknown' && crLink.motivo === 'piuProfili'
            ? t('crLink.moreProfiles', { n: crLink.n })
            : t('battles.identity.unknown')}
        </p>
      </div>
    );
  }
  if (profileError) {
    const code = isQbotApiError(profileError) ? profileError.data?.error?.code : undefined;
    if (code === 'NOT_LINKED' || code === 'NOT_FOUND') {
      // 🔴 DENTRO LA HUB NON SI NAVIGA VIA, e non e' una preferenza di stile.
      // Da rotta autonoma (`/cr/battles`) il redirect e' corretto. Ma montata nel pannello
      // della Hub l'URL e' fermo su `/`, e questo `replace` **sostituisce l'unica voce di
      // history**; `CRLink` fa a sua volta `<Navigate to="/duels/link" replace />` — due
      // `replace` in fila — e `useBackButton`, che chiama `navigate(-1)`, non ha piu' niente
      // da spopolare. L'utente tocca una scheda della Hub e resta chiuso fuori.
      // (Segnalato da Luca il 2026-09-10; `DecksPanel` aveva gia' preso la strada giusta e
      // se ne legge il motivo li': «rather than being bounced to /cr/link out of the Hub».)
      if (embedded) return <InvitoACollegare />;
      return <Navigate to="/cr/link" replace />;
    }
  }

  const onRowClick = (battle: CRBattle, absIdx: number) => {
    haptics.selection();
    navigate(`/cr/battles/${absIdx}`, { state: { battle, playerTag: tag } });
  };

  const switchView = (next: BattlesView) => {
    if (next === view) return;
    haptics.selection();
    dispatch(setBattlesView(next));
  };

  return (
    // NON scorre piu' questo contenitore: prima scorreva tutto insieme, quindi
    // la card di fondo scivolava via col contenuto e il suo bordo superiore
    // usciva dallo schermo — sembrava tagliata. Ora la card resta FERMA e
    // scorre solo cio' che sta dentro, quindi i suoi angoli arrotondati sono
    // sempre visibili e il contenuto sembra scorrere dentro una finestra.
    // py-1 e non py-4: incorporata nella Hub, il pannello ha gia' il suo
    // spazio sopra e sotto, e i due si sommavano staccando la card dalla barra
    // molto piu' delle altre sezioni. Da rotta autonoma (/cr/battles) resta un
    // filo d'aria, sufficiente perche' la chrome sopra ne porta gia'.
    <div className="flex h-full flex-col gap-3 overflow-hidden px-4 py-1">
      {/* Intestazione: a sinistra COSA guardo (cronologia o statistiche), a
          destra l'unica azione (aggiorna). Due gruppi separati e non una fila
          indistinta di bottoni: scegliere una vista e ricaricare i dati sono
          gesti di natura diversa, e mescolarli faceva sembrare "Aggiorna" una
          terza vista. */}
      <header className="flex shrink-0 items-center gap-2">
        <Segmented
          className="flex-1"
          value={view}
          onChange={switchView}
          options={(['list', 'stats'] as const).map((v) => ({
            value: v,
            label: t(`battles.seg.${v}`, { defaultValue: v === 'list' ? 'Battles' : 'Stats' }),
          }))}
        />
        {/* Contorno invece di superficie piena: è un'azione secondaria e non
            deve competere con la vista selezionata, che è piena e ciano. */}
        <button
          type="button"
          onClick={onRefresh}
          disabled={isFetching}
          aria-label={t('battles.refresh')}
          className="flex shrink-0 items-center gap-1.5 rounded-2xl px-4 py-2.5 text-sm font-bold text-fg ring-1 ring-white/20 transition-colors active:bg-white/5 disabled:opacity-50"
        >
          <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
          {t('battles.refresh')}
        </button>
      </header>

      {!!error && <p className="rounded-md bg-rose-500/10 p-3 text-sm text-rose-300">{t('battles.error')}</p>}

      {/* Tutto il contenuto vive dentro UNA superficie opaca: i filtri e le
          righe erano appoggiati direttamente sulla texture a scaglie, che
          passava dietro il testo. */}
      {/* La card prende l'altezza rimasta e CLIPPA; lo scorrimento vive nel div
          interno. `flush` toglie il padding alla card e lo sposta sullo
          scroller: cosi' il contenuto scorre fin sotto il bordo arrotondato
          invece di fermarsi a una fascia vuota. */}
      {/* NIENTE flex-1: la card CONTIENE, non riempie. Con flex-1 arrivava in
          fondo allo schermo anche con due righe dentro, e il vuoto sotto
          l'ultima riga sembrava contenuto mancante. Senza, prende l'altezza del
          suo contenuto e si ferma; quando il contenuto supera lo spazio
          disponibile il flex la fa rientrare (`flex: 0 1 auto`) e a quel punto
          scorre l'interno. */}
      <SectionCard flush className="flex min-h-0 flex-col overflow-hidden">
        <div className="min-h-0 overflow-y-auto overscroll-contain p-3 [touch-action:pan-y]">
          {isFetching && battles.length === 0 ? (
            <div className="px-4 py-6 text-sm text-fg-muted">{t('battles.loading')}</div>
          ) : view === 'list' ? (
            <BattlesListView battles={battles} playerTag={tag} onRowClick={onRowClick} />
          ) : (
            <BattlesStats battles={battles} playerTag={tag} />
          )}
        </div>
      </SectionCard>
    </div>
  );
}
